import {
  Alert,
  Box,
  Button,
  Card,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { UsersRound } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import type {
  SalesCampaignContactGroupResult,
  SalesHarvestContactRow,
  SalesHarvestOrg,
} from "@xop/dashboard-shared";

import { emailCampaignsPath } from "@crm-src/lib/email-campaign-routes";

import { useCan } from "@/auth/permissions";
import { BackButton } from "@/components/ui/back-button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { PageLoader } from "@/components/ui/page-loader";
import { Pagination } from "@/components/ui/pagination";

import { salesApi } from "../client.js";
import { SalesEmptyState, SalesErrorState } from "../components/SalesEmptyState.js";
import { verticalLabel } from "../components/VerticalBadge.js";
import { ContactDialog, type DialogContact } from "../contacts/ContactDialog.js";
import { ContactsTable, OrgsTable } from "../contacts/HarvestTables.js";
import { OrgDialog } from "../contacts/OrgDialog.js";
import { CampaignReport } from "./CampaignReport.js";
import { formatWindow, StatusChip } from "./campaignStatus.js";

const PER_PAGE = 25;

type View = "orgs" | "contacts";

/**
 * Sales ▸ Campaigns ▸ one campaign — `/sales/campaigns/:slug`.
 *
 * The Campaigns list shows a campaign's numbers; this page shows the RESULTS
 * behind them: every company and every person that campaign harvested, and
 * nothing from any other campaign (the agent filters on `campaign=<slug>`).
 * It works for a campaign whose definition was deleted — the harvested rows
 * keep the slug, so the page falls back to it for a title.
 *
 * "Create contact group" hands the campaign to Email Campaigns: one CRM
 * group, "Campaign: <label>", holding exactly this campaign's mailable
 * contacts. Pressing it again REBUILDS that group — it never mixes in another
 * campaign or the wider pool. Addresses the harvester only guessed on a domain
 * with no mail server are left out and counted, because mailing them bounces.
 */
export function CampaignResultsPage(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  // Both hooks always run — never short-circuit a hook call.
  const canEditSales = useCan("sales:edit");
  const canViewCrm = useCan("crm:view");
  const canBuildGroup = canEditSales && canViewCrm;

  const [view, setView] = useState<View>("orgs");
  const [vertical, setVertical] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [openOrgId, setOpenOrgId] = useState<string | null>(null);
  const [openContact, setOpenContact] = useState<DialogContact | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [built, setBuilt] = useState<SalesCampaignContactGroupResult | null>(null);

  // Same keys as the Campaigns list / its inline report, so both share cache.
  const campaigns = useQuery({
    queryKey: ["sales", "campaigns"],
    queryFn: () => salesApi.getCampaigns(),
  });
  const report = useQuery({
    queryKey: ["sales", "campaigns", slug, "report"],
    queryFn: () => salesApi.getCampaignReport(slug),
  });
  const stats = useQuery({
    queryKey: ["sales", "harvest", "stats"],
    queryFn: salesApi.getHarvestStats,
  });

  const filters = {
    campaign: slug,
    ...(vertical ? { vertical } : {}),
    ...(q ? { q } : {}),
    page,
    per_page: PER_PAGE,
  };
  const orgs = useQuery({
    queryKey: ["sales", "harvest", "orgs", filters],
    queryFn: () => salesApi.getHarvestOrgs(filters),
    enabled: view === "orgs",
    placeholderData: keepPreviousData,
  });
  const contacts = useQuery({
    queryKey: ["sales", "harvest", "contacts", filters],
    queryFn: () => salesApi.getHarvestContacts(filters),
    enabled: view === "contacts",
    placeholderData: keepPreviousData,
  });

  const buildGroup = useMutation({
    mutationFn: () => salesApi.createCampaignContactGroup(slug),
    onSuccess: (res) => setBuilt(res),
  });

  const campaign = campaigns.data?.campaigns.find((c) => c.slug === slug) ?? null;
  const label = campaign?.label || report.data?.label || slug;
  const active = view === "orgs" ? orgs : contacts;
  const industries = (report.data?.per_vertical ?? []).map((r) => r.vertical);
  const withEmail = report.data?.contacts.with_email ?? 0;

  const reset = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <BackButton to="/sales/campaigns" label="All campaigns" />
      </Box>

      <PageHeader
        title={label}
        description={
          campaign ? (
            <Stack component="span" direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <StatusChip campaign={campaign} />
              <span>{formatWindow(campaign)}</span>
              {campaign.goal && <span>· {campaign.goal}</span>}
            </Stack>
          ) : (
            "This campaign’s definition was deleted — everything it harvested is still here."
          )
        }
        actions={
          canBuildGroup ? (
            <Button
              variant="contained"
              startIcon={<UsersRound size={16} />}
              onClick={() => setConfirming(true)}
              disabled={report.isPending || withEmail === 0}
              data-testid="create-contact-group"
            >
              Create contact group
            </Button>
          ) : undefined
        }
      />

      {built && (
        <Alert severity="success" onClose={() => setBuilt(null)} data-testid="contact-group-built">
          <strong>{built.group.name}</strong> now holds {built.group.members_count.toLocaleString()}{" "}
          contact{built.group.members_count === 1 ? "" : "s"} — only this campaign’s.{" "}
          {built.skipped_unsendable > 0 && (
            <>
              {built.skipped_unsendable.toLocaleString()} address
              {built.skipped_unsendable === 1 ? " was" : "es were"} left out because the domain has
              no mail server or the verifier rejected it.{" "}
            </>
          )}
          {built.capped && "The campaign is larger than 10,000 contacts; the first 10,000 went in. "}
          Pick the group as the audience of an email campaign:{" "}
          <Link component={RouterLink} to={emailCampaignsPath("/create")}>
            new email campaign
          </Link>{" "}
          ·{" "}
          <Link component={RouterLink} to="/crm/contacts?tab=groups">
            see the group
          </Link>
          .
        </Alert>
      )}
      {buildGroup.isError && (
        <Alert severity="error" onClose={() => buildGroup.reset()}>
          Couldn’t create the contact group. {(buildGroup.error as Error).message}
        </Alert>
      )}

      <CampaignReport slug={slug} />

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v: View | null) => v && reset(() => setView(v))}
          aria-label="campaign results view"
        >
          <ToggleButton value="orgs">Companies</ToggleButton>
          <ToggleButton value="contacts">Contacts</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          select
          size="small"
          label="Industry"
          value={vertical}
          onChange={(e) => reset(() => setVertical(e.target.value))}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All industries</MenuItem>
          {industries.map((v) => (
            <MenuItem key={v} value={v}>
              {verticalLabel(v)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label={view === "orgs" ? "Search companies" : "Search people"}
          value={q}
          onChange={(e) => reset(() => setQ(e.target.value))}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      {active.isLoading && !active.data ? (
        <PageLoader />
      ) : active.isError ? (
        <SalesErrorState error={active.error} />
      ) : active.data && active.data.total === 0 ? (
        <SalesEmptyState
          title={q || vertical ? "Nothing matches" : "Nothing harvested for this campaign yet"}
          description={
            q || vertical
              ? "Clear the search or the industry filter to see the whole campaign."
              : "While the campaign is live the harvester finds companies in its sectors and digs out named people. They appear here as they are found."
          }
        />
      ) : (
        <Card variant="outlined" sx={{ overflow: "hidden" }}>
          {view === "orgs" ? (
            <OrgsTable
              items={(orgs.data?.items ?? []) as SalesHarvestOrg[]}
              onOpen={(id) => setOpenOrgId(id)}
            />
          ) : (
            <ContactsTable
              items={(contacts.data?.items ?? []) as SalesHarvestContactRow[]}
              onOpen={(c) => setOpenContact(c)}
            />
          )}
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
            <Pagination
              page={page}
              perPage={PER_PAGE}
              totalCount={active.data?.total ?? 0}
              onPageChange={setPage}
              entityNoun={view === "orgs" ? "companies" : "contacts"}
            />
          </Box>
        </Card>
      )}

      <OrgDialog
        orgId={openOrgId}
        onClose={() => setOpenOrgId(null)}
        onOpenContact={(c) => setOpenContact(c)}
      />
      <ContactDialog
        contact={openContact}
        verifierEnabled={stats.data?.verifier_enabled ?? false}
        onClose={() => setOpenContact(null)}
      />

      <ConfirmDialog
        isOpen={confirming}
        title={`Create a contact group from “${label}”?`}
        description={
          <>
            Puts this campaign’s contacts into the CRM group{" "}
            <strong>Campaign: {label}</strong> — and nobody else. If the group already exists it
            is rebuilt to match the campaign exactly. Addresses on a domain with no mail server are
            left out. Nothing is sent: you pick the group in an email campaign and press Send there.
          </>
        }
        confirmLabel="Create contact group"
        variant="default"
        // A failure closes the dialog too; the error Alert above says why.
        onConfirm={async () => {
          await buildGroup.mutateAsync().catch(() => undefined);
        }}
        onClose={() => setConfirming(false)}
      />
    </Box>
  );
}
